"use client"

import { useId } from "react"
import { useField } from "@payloadcms/ui"
import type { CheckboxFieldClientComponent } from "payload"

import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { FieldShell, resolveLabel } from "./field-shell"

/**
 * Shadcn-themed Payload Checkbox field. Layout differs slightly from
 * the other primitives — the label sits to the right of the box
 * (industry convention) instead of above it, but the description +
 * error rows still come from the shared FieldShell.
 */
export const ShadcnCheckboxField: CheckboxFieldClientComponent = (props) => {
  const { path, field } = props
  const fieldName = (field as { name?: string })?.name ?? path
  const { value, setValue, showError, errorMessage } = useField<boolean>({
    path: path ?? fieldName,
  })
  const id = useId()

  const label = resolveLabel(
    (field as { label?: unknown }).label,
    fieldName,
  )
  const description =
    (field as { admin?: { description?: unknown } })?.admin?.description
  const required = Boolean((field as { required?: boolean }).required)

  return (
    <FieldShell
      // No top-label — checkbox label is rendered next to the box below
      label={undefined}
      required={false}
      description={
        typeof description === "string" || typeof description === "object"
          ? (description as React.ReactNode)
          : undefined
      }
      error={errorMessage}
      showError={showError}
      className="adis-field--checkbox"
    >
      <div className="flex items-center gap-2 mt-0.5">
        <Checkbox
          id={id}
          checked={Boolean(value)}
          onCheckedChange={(checked) => setValue(Boolean(checked))}
          aria-invalid={showError ? true : undefined}
        />
        <Label
          htmlFor={id}
          className="cursor-pointer text-[13px] font-medium tracking-tight"
        >
          {label}
          {required ? (
            <span className="ml-0.5 text-destructive" aria-label="champ obligatoire">
              *
            </span>
          ) : null}
        </Label>
      </div>
    </FieldShell>
  )
}

export default ShadcnCheckboxField
